const DEFAULT_WISHLIST_NAME = 'My Wishlist';

// ECSR-1004: every word the heart speaks or shows comes from the locale files.
// snippets/wishlist-strings.liquid injects window.wishlistStrings (translated,
// entity-decoded) before this deferred script runs, in the same way the theme
// hands window.cartStrings and window.variantStrings to its own JavaScript.
// There are deliberately NO English fallbacks here: README §8 forbids hardcoded
// copy in the theme's scripts, so a missing string yields '' and every caller
// below leaves the markup's own (Liquid-rendered, translated) attribute alone
// rather than overwriting it with English.
// `[product]` is the placeholder the product title is substituted into, matching
// the theme's existing `| t: quantity: '[quantity]'` convention.
const wishlistString = (key, product) => {
  const strings = window.wishlistStrings || {};
  const value = typeof strings[key] === 'string' ? strings[key] : '';
  if (!value) return '';
  return product ? value.split('[product]').join(product) : value;
};

// ECSR-1004 (review): published so the Searchspring card renderer
// (assets/searchspring-collection-products.js) uses this one lookup rather than
// keeping a second copy of the same logic and placeholder convention. This file
// is deferred in layout/theme.liquid's <head>, that one in a body section, and
// deferred scripts run in document order, so it is assigned before that file runs.
window.wishlistString = wishlistString;

// ECSR-1004: screen-reader feedback. Swym's toast — and our own fallback toast —
// is purely visual, so saving or removing an item told assistive technology
// nothing at all. Every wishlist outcome is announced into a visually hidden
// live region.
//
// ECSR-1005: there are now TWO permanent regions — one always polite, one always
// assertive — instead of one region whose role and aria-live were retuned per
// message. Several screen reader and browser combinations do not reliably honour
// a live region changing its urgency after it already exists, so an error could
// be announced politely or dropped entirely. Nothing below ever sets role,
// aria-live, aria-atomic or aria-relevant on a region that exists: the regions
// are rendered, already typed, by snippets/wishlist-live-regions.liquid at the
// top of the body on every page, and the only writes here are to their text.
// The same hiding the theme's .visually-hidden class (assets/base.css) gives
// the rendered regions, written inline. It is used ONLY by the create-if-absent
// safety net below: a layout that did not render the snippet may equally not
// have loaded base.css, and a region relying on that class alone would then be
// visible text at the top of the page. It is NOT display:none, which would stop
// the region being announced at all.
const WISHLIST_LIVE_REGION_STYLE =
  'position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;';

const WISHLIST_LIVE_REGIONS = {
  polite: { id: 'wishlist-live-region-polite', role: 'status', live: 'polite' },
  assertive: { id: 'wishlist-live-region-assertive', role: 'alert', live: 'assertive' }
};

const getWishlistLiveRegion = (isError = false) => {
  const config = isError ? WISHLIST_LIVE_REGIONS.assertive : WISHLIST_LIVE_REGIONS.polite;
  const existing = document.getElementById(config.id);
  if (existing) return existing;
  if (!document.body) return null;
  // Safety net only, for a page whose layout did not render the snippet (a
  // custom layout, say). The role, urgency and hiding are all set BEFORE the
  // element is put in the document, so assistive technology sees the region
  // correctly typed the first time it sees it at all, and they are never
  // touched again. The class carries the theme's convention where base.css is
  // present; the inline style keeps the region hidden even where it is not.
  const region = document.createElement('div');
  region.id = config.id;
  region.className = 'visually-hidden';
  region.setAttribute('role', config.role);
  region.setAttribute('aria-live', config.live);
  region.style.cssText = WISHLIST_LIVE_REGION_STYLE;
  document.body.appendChild(region);
  return region;
};

// Both regions are confirmed present at page load, so neither can be created in
// the same moment as the message it has to carry.
const ensureWishlistLiveRegions = () => {
  getWishlistLiveRegion(false);
  getWishlistLiveRegion(true);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', ensureWishlistLiveRegions);
} else {
  ensureWishlistLiveRegions();
}

// The text is cleared first and re-set asynchronously so repeating the same
// action is announced again rather than being swallowed as an unchanged value.
// That is a text change only — it mutates no attribute of either region.
const announceWishlistStatus = (message, isError = false) => {
  if (!message) return;
  const region = getWishlistLiveRegion(isError);
  if (!region) return;
  region.textContent = '';
  setTimeout(() => {
    region.textContent = message;
  }, 50);
};

// ECSR-1004: the visual toast. It is aria-hidden because the live region above
// already announces the same message and announcing both would double-speak it.
//
// ECSR-998 (QA): it is now the ONLY toast the wishlist shows. It used to be a
// fallback, used only when Swym's own notification UI was absent, and Swym's toast
// was preferred whenever it existed. Swym positions that toast against its own
// injected .swym-button-bar, which this theme renders its own hearts instead of and
// hides outright (assets/theme2.css hides the injected bar inside product-form,
// assets/component-cart-drawer.css inside every cart line). A hidden anchor has a
// zero-sized rect, so on the product page the toast was laid out from x = 0 and its
// leading characters were cut off by the left edge of the viewport at 1440x900,
// while in the mini cart — where Swym injects no bar at all — it fell back to its own
// default position and rendered fully. Rendering the theme's own toast everywhere
// makes the position deterministic: fixed to the viewport's top-right corner, never
// dependent on an element the theme deliberately hides, and never clipped.
// It is also capped so a long translated string wraps inside the viewport instead of
// overflowing it.
const WISHLIST_TOAST_COLORS = {
  success: '#4CAF50',
  error: '#f44336',
  info: '#2196F3'
};

const showWishlistToast = (message, type) => {
  const notification = document.createElement('div');
  notification.className = `swym-custom-notification swym-${type}`;
  notification.textContent = message;
  notification.setAttribute('aria-hidden', 'true');
  notification.style.cssText = `position: fixed; top: 20px; right: 20px; left: auto; bottom: auto; max-width: min(36rem, calc(100vw - 4rem)); box-sizing: border-box; background: ${WISHLIST_TOAST_COLORS[type]}; color: white; padding: 15px 20px; border-radius: 4px; z-index: 9999; font-size: 14px;`;
  document.body.appendChild(notification);
  setTimeout(() => notification.remove(), 3000);
};

// ECSR-1004: one path for every wishlist outcome — Swym's toast when its UI is
// there, ours when it is not, and in both cases an announcement. The emoji stays
// here rather than in the locale files so the visible toast reads exactly as it
// always has while the announced text is not cluttered with "red heart".
// ECSR-1004 (review): a locale that ever lost one of these keys must not cost
// the sighted shopper the confirmation or error toast as well — the toast used
// to be unconditional. So the toast always appears; with no copy it falls back
// to the badge alone (kept here, not in the locale files, like the emoji above).
// Only the announcement, which is text and nothing else, is skipped: an empty
// live region says nothing anyway.
const WISHLIST_TOAST_FALLBACK_BADGE = {
  success: '\u2764\ufe0f',
  error: '\u26a0\ufe0f',
  info: '\u{1f499}'
};

const reportWishlistOutcome = (stringKey, { type = 'success', emoji = '' } = {}) => {
  const message = wishlistString(stringKey);
  const isError = type === 'error';
  const badge = emoji || (message ? '' : (WISHLIST_TOAST_FALLBACK_BADGE[type] || ''));
  const visibleMessage = [badge, message].filter(Boolean).join(' ');
  // ECSR-998 (QA): one toast, ours, everywhere — see showWishlistToast for why
  // Swym's anchored notification could not stay inside the viewport here.
  if (visibleMessage) showWishlistToast(visibleMessage, isError ? 'error' : type);
  announceWishlistStatus(message, isError);
};

// ECSR-1003: the cache starts as an empty list, not a non-array, so every
// reader's Array.isArray guard passes and a wishlist fetch that never resolves
// degrades to "hearts read as unsaved" instead of stalling the state machine
// site-wide.
window.wishlistContents = [];

// ECSR-1003: an empty cache is ambiguous on its own — it means "the shopper has
// saved nothing" only once the fetch has actually resolved. Until then it means
// "unknown", and a sweep that treats unknown as empty CLEARS a heart the shopper
// genuinely saved and rewires it back to addToWishlist (a silent duplicate add
// on the next click). These two flags make the three states distinguishable:
//   loaded false / failed false -> not loaded yet
//   loaded true                 -> the cache is authoritative
//   failed true                 -> the fetch failed; the cache stays unknown
//
// ECSR-1003 CONTRACT: until the cache is confirmed loaded it is a POSITIVE-ONLY
// source — its entries may FILL a heart, never CLEAR one, because while unloaded
// the only entries it holds are items this session genuinely saved. Once loaded
// it is authoritative and a sweep may clear as well as fill.
window.wishlistContentsLoaded = false;
window.wishlistContentsLoadFailed = false;

// ECSR-1003: the one place the two flags above are read. Everything that can
// CLEAR a heart from the cache asks this question instead of testing a flag, so
// there is a single definition of "authoritative"; the load-failure flag is read
// here so a failed fetch can never be mistaken for an empty wishlist.
window.wishlistCacheIsAuthoritative = () =>
  window.wishlistContentsLoaded === true && window.wishlistContentsLoadFailed !== true;

// ECSR-1003: removals recorded this session, declared ahead of the two helpers
// that maintain them. The server snapshot a fetch returns can predate a removal
// made while that fetch was in flight, so these are subtracted from the fetched
// list when it lands; a later re-add clears the entry again.
const wishlistItemsForgotten = [];
const wishlistItemMatches = (item, variantId, productId) => (
  variantId
    ? String(item.variantId) === String(variantId)
    : String(item.productId) === String(productId)
);
const isForgottenWishlistItem = (item) => wishlistItemsForgotten.some(
  ({ variantId, productId }) => wishlistItemMatches(item, variantId, productId)
);
// ECSR-1003: an explicit two-id comparison rather than reusing
// wishlistItemMatches with the arguments swapped round. Both entries here are
// records of the same shape, so compare them field for field: same identity rule
// as wishlistItemMatches (variant-first, product id only when there is no
// variant) but readable without having to work out which side is which.
const sameForgottenWishlistItem = (a, b) => (
  a.variantId && b.variantId
    ? String(a.variantId) === String(b.variantId)
    : String(a.productId) === String(b.productId)
);
const unforgetWishlistItem = ({ variantId, productId }) => {
  for (let i = wishlistItemsForgotten.length - 1; i >= 0; i -= 1) {
    if (sameForgottenWishlistItem({ variantId, productId }, wishlistItemsForgotten[i])) {
      wishlistItemsForgotten.splice(i, 1);
    }
  }
};

// ECSR-1003: keep the cache authoritative for items saved after (or instead of)
// the fetch, mirroring the .filter(...) the removal path already performs.
// Without this a heart filled by an add is invisible to every cache reader.
window.rememberWishlistItem = ({ variantId, productId, url }) => {
  if (!Array.isArray(window.wishlistContents)) return;
  if (!variantId && !productId) return;
  // ECSR-1003: an item removed and then saved again this session is no longer
  // forgotten, or the merge below would subtract it from the fetched list and the
  // authoritative sweep would clear the heart the shopper just re-filled.
  unforgetWishlistItem({ variantId, productId });
  const alreadySaved = window.wishlistContents.some(
    (item) => String(item.variantId) === String(variantId) &&
      String(item.productId) === String(productId)
  );
  if (alreadySaved) return;
  window.wishlistContents.push({ variantId, productId, url });
};

// ECSR-1003: the mirror of rememberWishlistItem. A removal made anywhere — our own
// removeFromWishlist success path, or Swym's own UI via sw:removedfromlist — has to
// leave the cache, otherwise a later sweep re-fills the heart from a stale entry.
window.forgetWishlistItem = ({ variantId, productId }) => {
  if (!variantId && !productId) return;
  // ECSR-1003: one entry per removal. Our own removeFromWishlist success path and
  // Swym's sw:removedfromlist event both call this for the same removal, so an
  // unguarded push recorded every removal twice and a later re-add had to splice
  // twice to stop the fetch merge subtracting an item the shopper had saved again.
  const alreadyForgotten = wishlistItemsForgotten.some(
    (entry) => sameForgottenWishlistItem({ variantId, productId }, entry)
  );
  if (!alreadyForgotten) wishlistItemsForgotten.push({ variantId, productId });
  if (!Array.isArray(window.wishlistContents)) return;
  // Variant-first, matching isVariantInWishlist: a variant id identifies the entry
  // on its own, and the product id is only used for entries with no variant.
  window.wishlistContents = window.wishlistContents.filter(
    (item) => !wishlistItemMatches(item, variantId, productId)
  );
};

window.addToWishlist = ({variantId, productId, url}) => {
  // The Swym SDK is withheld until the shopper's cookie consent allows it. Never throw here:
  // let the theme's consent notice (snippets/wishlist-consent-notice.liquid) explain instead.
  if (!window._swat || typeof window._swat.addToList !== 'function') {
    document.dispatchEvent(new CustomEvent('wishlist:blocked', { detail: { variantId, productId, reason: 'swym-unavailable' } }));
    return;
  }
  // Ensure wishlist ID exists before trying to add items
  const performAdd = (listId) => {
    window._swat.addToList(listId, {epi: variantId, empi: productId, du: url}, (response) => {
      // ECSR-1004: shows the toast (Swym's or ours) AND announces the outcome.
      reportWishlistOutcome('added', { type: 'success', emoji: '❤️' });
      // ECSR-1003: the cache is the source of truth for every later sweep, so an
      // item saved here has to go into it or the next resync clears this heart.
      window.rememberWishlistItem({ variantId, productId, url });
      updateWishlistButtonState(variantId, productId, true);
    }, (error) => {
      console.error('Swym wishlist error:', error);
      // ECSR-1004: errors are announced assertively, not shown silently.
      reportWishlistOutcome('addError', { type: 'error' });
    });
  };

  // Check if wishlist ID already exists
  if (window._swat.swymCustomWishlistSelectedListId) {
    performAdd(window._swat.swymCustomWishlistSelectedListId);
  } else {
    // Create/get wishlist first, then add item
    window.ensureSingleWishlist(DEFAULT_WISHLIST_NAME, function(res) {
      if (res?.lid) {
        window._swat.swymCustomWishlistSelectedListId = res.lid;
        performAdd(res.lid);
      } else {
        console.error('Failed to create/get wishlist');
        reportWishlistOutcome('unavailable', { type: 'error' });
      }
    });
  }
};

// ECSR-1000: does this heart carry a variant of its own? Mini cart lines, PDP
// hearts and product cards all render a variantId; hearts that do not are the
// only ones allowed to fall back to product-level matching.
const hasVariantContext = (onclickAttr) => /variantId:\s*['"]?\d/.test(onclickAttr);

// ECSR-1001 (mirrors ECSR-606 on JA US): anchored id matching. The previous
// `includes('variantId: 123')` substring test also matched a rendered
// `variantId: 1234`, so a wishlist event for the shorter id flipped — and
// rewrote the onclick ids of — the heart belonging to the longer id. That only
// bites when one id on the page is a strict numeric prefix of another, but the
// failure is exactly the mis-wiring ECSR-1000 fixed. Requiring a non-digit
// after the id makes the match exact, and covers both render forms in this
// theme: unquoted (snippets/buy-buttons.liquid) and quoted
// (snippets/cart-drawer.liquid, snippets/card-product.liquid,
// assets/searchspring-collection-products.js).
const onclickIdMatches = (onclickAttr, key, id) => {
  // Ids come from Liquid and are always numeric; refuse anything else (including
  // the empty id Searchspring can render) rather than interpolating it into a RegExp.
  if (!/^\d+$/.test(String(id ?? ''))) return false;
  return new RegExp(`${key}:\\s*['"]?${id}(?!\\d)`).test(onclickAttr);
};

// ECSR-1000: wishlist state must be read from the VARIANT a heart represents.
// Matching on the product made every sibling variant of a saved variant render
// as saved, so a mini cart line for an unsaved variant looked saved and could
// not be saved. The product id is only consulted for hearts with no variant.
const isVariantInWishlist = (variantId, productId, wishlistItems) => {
  if (!Array.isArray(wishlistItems)) return false;
  if (variantId) {
    return wishlistItems.some(item => String(item.variantId) === String(variantId));
  }
  return Boolean(productId) &&
    wishlistItems.some(item => String(item.productId) === String(productId));
};

// ECSR-1002 (mirrors ECSR-604 on JA US): hearts already rewritten to
// removeFromWishlist must stay addressable. The old `[onclick*="addToWishlist"]`
// selector stopped matching a heart the moment it flipped to saved, and the mini
// cart and product-card hearts carry no `AddToWishlistButton` id either, so once
// saved they dropped out of every query and no later wishlist event could return
// them to unsaved.
// ECSR-1002: `goToWishlistPage` is included for the same reason — a saved heart
// we could not wire to an in-place removal must still be findable so it can be
// returned to unsaved.
// ECSR-1002: `root` scopes the query. The cart-update resync only needs the cart
// drawer subtree; running it over the whole document on every quantity nudge
// touched every product card on a collection page for nothing.
const getAllWishlistButtons = (root = document) => {
  const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
  const wishlistButtons = scope.querySelectorAll(
    '[onclick*="addToWishlist"], [onclick*="removeFromWishlist"], [onclick*="goToWishlistPage"]'
  );
  const idButtons = scope.querySelectorAll('[id*="AddToWishlistButton"]');
  return [...wishlistButtons, ...idButtons];
};

// ECSR-1002: rewriting a heart's onclick must not throw away the product url
// Liquid rendered into it (cart drawer, product cards and Searchspring cards all
// quote the product's own url; only the PDP heart ships window.location.href).
// The original expression is stashed on the button the first time we touch it and
// reused for every later rewrite, so a heart toggled off and on again still saves
// the product's deep link rather than whatever page it was clicked from.
const wishlistButtonUrlExpression = (button) => {
  if (!button.dataset.wishlistUrl) {
    const onclickAttr = button.getAttribute('onclick');
    const urlMatch = onclickAttr ? onclickAttr.match(/url:\s*(['"])(.*?)\1/) : null;
    if (urlMatch) button.dataset.wishlistUrl = urlMatch[2];
  }
  return button.dataset.wishlistUrl
    ? `'${button.dataset.wishlistUrl}'`
    : 'window.location.href';
};

// ECSR-1002: ids are always numeric here; anything else (the empty variant id a
// Searchspring card or an out-of-stock card_product can render) is not usable in
// an onclick expression.
const numericWishlistId = (value) => (/^\d+$/.test(String(value ?? '')) ? String(value) : null);

// ECSR-1002: the add expression Liquid (or Searchspring) rendered, stashed the
// first time we touch the button, so a heart we could not rewire can always be
// put back exactly as it shipped when its item leaves the wishlist.
const rememberWishlistAddOnclick = (button) => {
  if (!button.dataset.wishlistAddOnclick) {
    const onclickAttr = button.getAttribute('onclick');
    if (onclickAttr && onclickAttr.includes('addToWishlist')) {
      button.dataset.wishlistAddOnclick = onclickAttr;
    }
  }
  return button.dataset.wishlistAddOnclick || null;
};

// ECSR-998 (release review): does the markup's own add expression name a
// variant? A card rendered with an empty variant id does not, and such a heart
// must keep its own expression rather than be rebound to whichever variant an
// event supplied.
const wishlistAddOnclickHasVariant = (addOnclick) =>
  /variantId:\s*['"]*\d/.test(String(addOnclick || ''));

// ECSR-1002: the manage-on-the-wishlist-page affordance kept for saved hearts we
// cannot wire to an in-place removal (no numeric variant id anywhere on the
// card). Named rather than inlined so getAllWishlistButtons can still find these
// hearts and return them to unsaved later.
window.goToWishlistPage = () => {
  window.location.href = '/pages/wishlist';
};

// ECSR-1002: the single place that flips a heart between its two states. The
// onclick MUST be rewritten alongside the visual state: rendered markup always
// ships onclick="addToWishlist(...)" (Liquid has no server-side knowledge of
// wishlist state), so a filled heart left wired to addToWishlist performs a
// silent duplicate add instead of a removal.
// ECSR-1004: per-product accessible names. Liquid (and the Searchspring card
// renderer) put the product title on the heart as data-wishlist-product-title, so
// a mini cart or a collection grid no longer presents a column of buttons that all
// announce the identical "Add to wishlist". The named form is used whenever the
// title is there; hearts rendered without it fall back to the generic wording.
// This is read only by applyWishlistButtonState, so heart state still has exactly
// one writer.
const wishlistButtonLabel = (button, namedKey, genericKey) => {
  const productTitle = button.dataset ? button.dataset.wishlistProductTitle : '';
  const named = productTitle ? wishlistString(namedKey, productTitle) : '';
  return named || wishlistString(genericKey);
};

// ECSR-998 (release review): `forceAddOnclickRebuild` is passed only by the PDP
// path, which knows the selected variant from the variant input and is therefore
// authoritative for it. buy-buttons.liquid renders the variant id UNQUOTED, so a
// product whose selected_or_first_available_variant is nil ships a syntactically
// broken `variantId: ,` expression; stash-wins would write that broken string
// back and leave the heart a dead control. Cards keep stash-wins (their ids are
// quoted, so a variant-less card legitimately represents the product and must not
// be rebound to whichever variant an event supplied).
const applyWishlistButtonState = (button, variantId, productId, isInWishlist, options = {}) => {
  const urlExpression = wishlistButtonUrlExpression(button);
  const addOnclick = rememberWishlistAddOnclick(button);
  const resolvedVariantId = numericWishlistId(variantId);
  const resolvedProductId = numericWishlistId(productId);
  const canRewriteOnclick = Boolean(resolvedVariantId && resolvedProductId);

  let title;
  let label;
  let onclick;

  if (isInWishlist && canRewriteOnclick) {
    // ECSR-1002: the saved heart now removes in place, so it no longer describes
    // itself as a link to the wishlist page.
    // ECSR-1004: the accessible name names the product and follows the saved
    // state, so the shopper hears "Remove <product> from wishlist" rather than a
    // generic label identical to every other heart on the page.
    title = wishlistString('remove');
    label = wishlistButtonLabel(button, 'removeProduct', 'remove');
    onclick = `removeFromWishlist({ variantId: ${resolvedVariantId}, productId: ${resolvedProductId}, url: ${urlExpression} })`;
  } else if (isInWishlist) {
    // ECSR-1002: saved, but there are no ids to remove with, so keep the
    // pre-existing manage-in-the-wishlist affordance. The heart must never keep
    // the add wiring while it reads as saved (that was a silent duplicate add)
    // and must never claim to remove what it cannot remove. The product id stays
    // in the expression so updateWishlistButtonState can still address this heart
    // and return it to unsaved on a later event.
    title = wishlistString('manage');
    label = wishlistButtonLabel(button, 'manageProduct', 'manageItem');
    onclick = resolvedProductId
      ? `goToWishlistPage({ productId: ${resolvedProductId} })`
      : 'goToWishlistPage()';
  } else {
    title = wishlistString('add');
    label = wishlistButtonLabel(button, 'addProduct', 'add');
    // Restore the markup's own add expression when we cannot rebuild one, so a
    // heart that carried the manage wiring does not keep it once unsaved.
    // ECSR-998 (release review): a heart whose own add expression carried no
    // numeric variant id (an out-of-stock card_product, a Searchspring card)
    // represents the product, not a variant. Rebuilding its expression from the
    // variant id that happened to arrive on the Swym event would bind the card
    // to that one variant for good, so its stashed expression wins.
    const rebuildAddOnclick = canRewriteOnclick &&
      (Boolean(options.forceAddOnclickRebuild) || !addOnclick ||
        wishlistAddOnclickHasVariant(addOnclick));
    onclick = rebuildAddOnclick
      ? `addToWishlist({ variantId: ${resolvedVariantId}, productId: ${resolvedProductId}, url: ${urlExpression} })`
      : addOnclick;
  }

  // ECSR-1002: nothing to do when the heart already reads and behaves this way.
  // The cart-update resync runs on every quantity nudge, so the common case is a
  // heart that is already correct.
  const alreadySaved = button.classList.contains('swym-added');
  if (alreadySaved === Boolean(isInWishlist) &&
      (!title || button.getAttribute('title') === title) &&
      (!label || button.getAttribute('aria-label') === label) &&
      (!onclick || button.getAttribute('onclick') === onclick)) {
    return;
  }

  // ECSR-1002: a saved item always reads as saved. Gating the filled heart on our
  // ability to rewrite the onclick made product-level matches render as unsaved,
  // so a product the shopper had saved looked unsaved and saving it again
  // duplicated it.
  button.classList.toggle('swym-added', Boolean(isInWishlist));
  // ECSR-1004: an unavailable string (window.wishlistStrings missing) leaves the
  // markup's own translated attribute in place rather than replacing it with
  // English or with "undefined".
  if (title) button.setAttribute('title', title);
  if (label) button.setAttribute('aria-label', label);
  if (onclick) button.setAttribute('onclick', onclick);
};

window.updateWishlistButtonState = (variantId, productId, isInWishlist = true) => {
  getAllWishlistButtons().forEach(button => {
    const onclickAttr = button.getAttribute('onclick');
    if (!onclickAttr) return;

    // ECSR-1000: only hearts for the SAME variant flip. Previously a productId
    // match flipped sibling-variant hearts too (and rewrote their onclick with
    // this variant's ids), so saving one colour marked another colour's cart
    // line as saved and stole its add action.
    // ECSR-1001: anchored, so an event for id N cannot flip a heart whose
    // rendered id merely starts with N.
    const variantMatches = onclickIdMatches(onclickAttr, 'variantId', variantId);
    const productMatches = onclickIdMatches(onclickAttr, 'productId', productId);

    if (variantMatches || (!hasVariantContext(onclickAttr) && productMatches)) {
      applyWishlistButtonState(button, variantId, productId, isInWishlist);
    }
  });
};



// ECSR-1002 (mirrors ECSR-604 on JA US): activating a saved heart removes the
// item IN PLACE via Swym's deleteFromList — no toast-then-redirect to
// /pages/wishlist. A shopper tapping a filled heart in the mini cart was
// previously navigated away from their cart mid-purchase. Removal touches the
// wishlist only: no cart request is made and the drawer is never opened, closed
// or re-rendered.
//
// The redirect is kept as a fallback for the case where Swym's delete API or the
// list id genuinely is not available, so the heart never becomes a dead control.
// ECSR-1002: removals currently awaiting a Swym callback, keyed on the ids the
// heart carries. Without this a double-tap issued two deleteFromList calls for
// the same item: the first succeeded and flipped the heart to unsaved, then the
// second's error callback raised "Error removing from wishlist" over a removal
// that had actually worked. The window is widest in the mini cart and on a slow
// connection.
const wishlistRemovalsInFlight = new Set();
// Safety valve: if Swym never calls either callback, the key is released so the
// heart stays usable rather than being permanently inert.
const WISHLIST_REMOVAL_TIMEOUT_MS = 10000;

window.removeFromWishlist = ({variantId, productId, url}) => {
  const inFlightKey = `${variantId}|${productId}`;
  if (wishlistRemovalsInFlight.has(inFlightKey)) return;

  let inFlightTimer = null;
  const clearInFlight = () => {
    if (inFlightTimer) clearTimeout(inFlightTimer);
    inFlightTimer = null;
    wishlistRemovalsInFlight.delete(inFlightKey);
  };
  const markInFlight = () => {
    wishlistRemovalsInFlight.add(inFlightKey);
    inFlightTimer = setTimeout(clearInFlight, WISHLIST_REMOVAL_TIMEOUT_MS);
  };
  markInFlight();

  const redirectToWishlist = () => {
    // The page is about to navigate, but release the key in case it does not.
    clearInFlight();
    reportWishlistOutcome('redirecting', { type: 'info', emoji: '💙' });

    setTimeout(() => {
      window.location.href = '/pages/wishlist';
    }, 1000);
  };

  // Swym identifies a list entry by its deep link as well as by its ids, so use
  // the url the wishlist itself recorded in preference to the page the heart
  // happened to be clicked from.
  const resolveDeleteUrl = () => {
    if (Array.isArray(window.wishlistContents)) {
      const saved = window.wishlistContents.find(
        (item) => String(item.variantId) === String(variantId)
      );
      if (saved && saved.url) return saved.url;
    }
    return url;
  };

  const performRemove = (listId) => {
    window._swat.deleteFromList(listId, {epi: variantId, empi: productId, du: resolveDeleteUrl()}, () => {
      clearInFlight();
      reportWishlistOutcome('removed', { type: 'info', emoji: '💔' });

      // Keep the local cache in step so other hearts for the same variant on this
      // page resolve correctly without waiting for a refetch.
      window.forgetWishlistItem({ variantId, productId });

      // Flips this heart back to unsaved and rewires its onclick to addToWishlist.
      updateWishlistButtonState(variantId, productId, false);

      // Refresh the wishlist grid if we happen to be on a page that renders it.
      if (typeof window.renderWishlistItems === 'function' && Array.isArray(window.wishlistContents)) {
        window.renderWishlistItems(window.wishlistContents);
      }
    }, (error) => {
      clearInFlight();
      console.error('Swym wishlist remove error:', error);
      reportWishlistOutcome('removeError', { type: 'error' });
    });
  };

  if (!window._swat || typeof window._swat.deleteFromList !== 'function') {
    redirectToWishlist();
    return;
  }

  if (window._swat.swymCustomWishlistSelectedListId) {
    performRemove(window._swat.swymCustomWishlistSelectedListId);
  } else {
    // Normally already resolved at page init; resolve on demand if not.
    // ECSR-1002: the third argument is required. ensureSingleWishlist's own error
    // paths (fetchLists, createList, updateListDetails) never call callbackFn, so
    // without an errorFn a failed lookup — offline, 5xx, blocked request, the
    // likeliest way the list id is unavailable in production — ran neither the
    // removal nor the redirect and the filled heart did nothing at all.
    window.ensureSingleWishlist(DEFAULT_WISHLIST_NAME, function (res) {
      if (res?.lid) {
        window._swat.swymCustomWishlistSelectedListId = res.lid;
        performRemove(res.lid);
      } else {
        console.error('Failed to resolve wishlist for removal');
        redirectToWishlist();
      }
    }, function (err) {
      console.error('Error resolving wishlist for removal', err);
      // ECSR-1002: ensureSingleWishlist sets swymCustomWishlistSelectedListId
      // before it tries to rename the list, so a rename failure leaves us with a
      // perfectly good list id. Removing in place with it keeps criterion 1 met;
      // the redirect is only for the case where the id genuinely is unknown.
      const knownListId = window._swat && window._swat.swymCustomWishlistSelectedListId;
      if (knownListId) {
        performRemove(knownListId);
        return;
      }
      redirectToWishlist();
    });
  }
};



// ECSR-1002: `root` limits the sweep to a subtree (the cart drawer, for the
// cart-update resync). It defaults to the whole document for the page-load and
// wishlist-fetch callers, which do need every heart on the page.
window.updateWishlistButtonsFromContents = (wishlistItems, root = document, options = {}) => {
  if (!wishlistItems || !Array.isArray(wishlistItems)) return;

  // ECSR-1003: `additive` implements the positive-only contract at the top of this
  // file. In additive mode this sweep only FILLS hearts whose ids appear in the
  // snapshot and leaves every other heart exactly as rendered, so it can never
  // clear a heart on the strength of a snapshot that may be incomplete. The caller
  // declares it, because the caller is the only party that knows whether the list
  // it is passing is authoritative.
  //
  // ECSR-1003: every sweep in the theme now passes
  // `additive: !window.wishlistCacheIsAuthoritative()`, directly or through
  // resyncWishlistButtons, so the predicate is the single gate on clearing
  // anywhere. The four callers are: fetchWishlistContents' success path below,
  // resyncWishlistButtons (cart drawer and any subtree handed to it), the
  // Searchspring re-render sweep in
  // assets/searchspring-collection-products.js, and the related/recent/featured
  // sweep in sections/related-products.liquid — the last two through
  // resyncWishlistButtons. An earlier version of this comment certified the
  // Searchspring sweep as authoritative: it was not. It swept the WHOLE document
  // against its own Swym snapshot, never asked the predicate, and so cleared
  // hearts even after the theme's own fetch had failed.
  const additive = options.additive === true;

  getAllWishlistButtons(root).forEach(button => {
    const onclickAttr = button.getAttribute('onclick');
    if (!onclickAttr) return;
    
    // ECSR-1003: one pattern each. `['"]*` matches zero or more quotes, so it
    // already covers the unquoted form the second alternative was written for;
    // that alternative could never run and implied two render forms needed two
    // patterns.
    const variantMatch = onclickAttr.match(/variantId:\s*['"]*(\d+)['"]*/);
    const productMatch = onclickAttr.match(/productId:\s*['"]*(\d+)['"]*/);
    
    if (variantMatch || productMatch) {
      const variantId = variantMatch ? variantMatch[1] : null;
      const productId = productMatch ? productMatch[1] : null;
      // ECSR-1000: variant-first. A heart that names a variant is saved only if
      // that exact variant is in the wishlist; the product id is the fallback
      // for hearts rendered without any variant context.
      const isInWishlist = isVariantInWishlist(variantId, productId, wishlistItems);

      // ECSR-1003: positive-only — nothing to add for this heart, and we are not
      // entitled to take anything away.
      if (additive && !isInWishlist) return;

      // ECSR-1002: hearts that read as already saved when the page loads have to
      // be wired to removeFromWishlist too, otherwise the first activation of a
      // saved heart re-adds the item instead of removing it.
      applyWishlistButtonState(button, variantId, productId, isInWishlist);
    }
  });
};

// ECSR-1003: `coversAllLists` says whether the list being fetched is the only one
// the shopper has. It gates the authoritative flag and nothing else, so the
// contract's second half ("once loaded it is authoritative") is only ever
// asserted over a cache that really does hold everything the shopper saved.
// ensureSingleWishlist RENAMES one list, it does not merge the others' contents,
// so with two or more Swym lists this cache is complete for one of them only.
// Chosen over unioning every list's details because it is a one-line guard with
// no extra round trips and no partial-failure case: with several lists every
// sweep simply stays additive for the session, which can fill a heart but can
// never clear one — the failure mode this epic exists to prevent. Unioning would
// mean N fetchListDetails calls whose own failures would need their own
// completeness accounting.
window.fetchWishlistContents = (listId, { coversAllLists = true } = {}) => {
  const onWishlistLoaded = (details) => {
    const fetched = (details && Array.isArray(details.items) ? details.items : []).map(item => ({
      name: item?.dt,
      price: item?.pr,
      image: item?.iu,
      url: item?.du,
      variantId: item?.epi,
      productId: item?.empi
    }));

    // ECSR-1003: MERGE, do not overwrite. Two Swym round trips (fetchLists, then
    // the rename) precede fetchListDetails, so on a slow connection there are
    // seconds in which the shopper can save an item; rememberWishlistItem pushed
    // it into the cache, and a server snapshot taken before that add does not
    // carry it. Overwriting dropped it, and the now-authoritative sweep below then
    // cleared that heart and re-wired it to addToWishlist — the next tap saved a
    // silent duplicate. Removals made in the same window are subtracted for the
    // same reason: the snapshot can predate them too. Dedupe is on variant+product,
    // the same identity rememberWishlistItem uses.
    const pending = Array.isArray(window.wishlistContents) ? window.wishlistContents : [];
    const merged = fetched.filter((item) => !isForgottenWishlistItem(item));
    pending.forEach((item) => {
      if (isForgottenWishlistItem(item)) return;
      const alreadyKnown = merged.some((known) => (
        String(known.variantId) === String(item.variantId) &&
        String(known.productId) === String(item.productId)
      ));
      if (!alreadyKnown) merged.push(item);
    });
    window.wishlistContents = merged;

    // ECSR-1003: only now is an empty cache meaningfully empty — and only if this
    // list is the shopper's only list. With a second Swym list the cache is
    // genuinely incomplete, so the flag stays false and every sweep stays
    // additive for the session: hearts for the other list's items are never
    // filled, but neither are they cleared and re-wired to add.
    window.wishlistContentsLoaded = coversAllLists === true;
    window.wishlistContentsLoadFailed = false;

    updateWishlistButtonsFromContents(window.wishlistContents, document, {
      additive: !window.wishlistCacheIsAuthoritative()
    });
    renderWishlistItems(window.wishlistContents);
  };

  // ECSR-1003: the error path was an empty function. A failed fetch left every
  // heart on the site showing the unsaved state with no signal anywhere that
  // the wishlist never loaded.
  const onWishlistLoadFailed = (error) => {
    // ECSR-1003: record the failure distinctly. The cache stays [] but
    // wishlistContentsLoaded stays false, so "the wishlist never loaded" is not
    // read downstream as "the shopper has saved nothing" — the cache-driven
    // sweeps stay parked rather than clearing hearts. No retry here by design.
    window.wishlistContentsLoadFailed = true;
    console.error('[wishlist] Could not load wishlist contents; hearts will show unsaved state.', error);
  };

  window._swat.fetchListDetails({
    lid: listId
  }, onWishlistLoaded, onWishlistLoadFailed);
};

window.renderWishlistItems = (items) => {
  const container = document.getElementById('account-wishlist-render-container');
  if (!container) return;
  
  // ECSR-1003: entries written by rememberWishlistItem carry ids and a url only —
  // no name, price or image, because an add gives us none. The grid cannot render
  // them: they came out as the literal strings "Product" and "Price not available"
  // over a grey placeholder tile, linking to "#". Placeholder copy standing in for
  // a real product is worse than omitting it, and this theme ships 51 locales.
  // Skip the stubs. They are NOT upgraded later in this page's life: the wishlist
  // fetch runs once per page load, before these adds happen, so a stub stays a
  // stub until the shopper navigates and the next page's fetch returns the item
  // with its name, price and image. The grid is short one tile until then; the
  // heart itself is filled and correct, because the stub is still in the cache
  // that every sweep reads.
  const renderableItems = items.filter((item) => item && (item.name || item.price));

  const displayItems = renderableItems.slice(0, 5);
  
  // ECSR-998 (release review): the last of this file's hardcoded English. These
  // four strings go through the same locale-backed lookup as every other word the
  // wishlist speaks (README §8), so the account wishlist grid is translated like
  // the hearts are.
  if (displayItems.length === 0) {
    // ECSR-998 (release review): with window.wishlistStrings unavailable the
    // lookup returns '', and rendering the block anyway left an empty paragraph
    // in an otherwise blank panel. Omitting an attribute is a quiet degrade;
    // a blank panel is not. Render nothing rather than an empty message.
    const emptyMessage = wishlistString('empty');
    container.innerHTML = emptyMessage
      ? `<div class="tw-text-center tw-py-8"><p class="tw-text-gray-600">${emptyMessage}</p></div>`
      : '';
    return;
  }
  
  const itemsHtml = displayItems.map(item => {
    const productName = item.name || wishlistString('productFallback');
    const productPrice = item.price ? window.Shopify.formatMoney(item.price * 100) : wishlistString('priceUnavailable');
    const productImage = item.image || '';
    const productUrl = item.url || '#';
    const nameParts = productName.split(/\n|\r\n|\r/);
    const title = nameParts[0] || productName;
    const subtitle = nameParts[1] || '';
    
    return `
      <div class="tw-text-center">
        <a href="${productUrl}" class="tw-block">
          <div class="tw-bg-gray-200 tw-aspect-square tw-mb-4 tw-flex tw-items-center tw-justify-center tw-overflow-hidden">
            ${productImage ? 
    `<img src="${productImage}" alt="${title}" class="tw-w-full tw-h-full tw-object-cover">` : 
    `<span class="tw-text-gray-500">${wishlistString('imagePlaceholder')}</span>`
}
          </div>
          <h3 class="tw-font-medium tw-text-gray-900 tw-mb-1">${title}</h3>
          ${subtitle ? `<p class="tw-text-gray-900 tw-mb-1">${subtitle}</p>` : ''}
          <p class="tw-text-gray-900">${productPrice}</p>
        </a>
      </div>
    `;
  }).join('');
  
  container.innerHTML = `<div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">${itemsHtml}</div>`;
};

window.updateProductFormWishlistButton = () => {
  const variantInput = document.querySelector('.product-variant-id');
  const wishlistButton = document.querySelector('[id*="AddToWishlistButton"]');
  
  if (!variantInput || !wishlistButton) return;
  
  const currentVariantId = variantInput.value;
  if (!currentVariantId) return;
  
  const onclickAttr = wishlistButton.getAttribute('onclick');
  const productMatch = onclickAttr ? onclickAttr.match(/productId:\s*['"]*(\d+)['"]*/) : null;
  const productId = productMatch ? productMatch[1] : null;
  // ECSR-1003: which variant does the heart currently represent? If the shopper has
  // switched to a different one, the heart's saved state belonged to the OLD variant
  // and must be recomputed in full — re-rendering for a different item is not the
  // same thing as clearing a saved heart, so the positive-only rule does not apply.
  const renderedVariantMatch = onclickAttr ? onclickAttr.match(/variantId:\s*['"]*(\d+)['"]*/) : null;
  const renderedVariantId = renderedVariantMatch ? renderedVariantMatch[1] : null;
  
  if (!productId) return;
  
  // ECSR-998 (release review): the onclick is NOT written here. Rewriting it to
  // add the newly selected variant and then returning at one of the gates below
  // left a filled heart wired to add — the silent duplicate add this epic fixed
  // five times — whenever the wishlist cache had not loaded. Every attribute of
  // this heart, onclick included, is written by applyWishlistButtonState, which
  // rebuilds the add expression from the ids it is handed, so the single call at
  // the end of this function refreshes the expression for the selected variant.
  const cacheLoaded = Array.isArray(window.wishlistContents);

  // ECSR-1000: the PDP heart always represents the currently selected
  // variant, so a saved sibling variant must not fill it.
  const isInWishlist = cacheLoaded &&
    isVariantInWishlist(currentVariantId, null, window.wishlistContents);
  const variantChanged = Boolean(renderedVariantId) &&
    String(renderedVariantId) !== String(currentVariantId);

  // ECSR-1003: the contract. Filling is always allowed. Clearing is allowed only
  // when the cache is authoritative, or when the heart now represents a different
  // variant than the state on it does — otherwise a heart the shopper just saved
  // would be stripped while the wishlist state is still unknown.
  // ECSR-998: when we may not clear, the heart keeps the state it is showing and
  // the call below still refreshes its expression for the selected variant — the
  // heart is never left saying one thing and doing another.
  const mayShowUnsaved = variantChanged ||
    (cacheLoaded && window.wishlistCacheIsAuthoritative());
  const nextState = isInWishlist ||
    (!mayShowUnsaved && wishlistButton.classList.contains('swym-added'));

  // ECSR-1002: same flip as every other heart, so the PDP heart for a saved
  // variant removes in place rather than describing a trip to the wishlist page.
  // ECSR-998 (release review): forceAddOnclickRebuild — this path resolved the
  // variant from the variant input the swatch picker writes, so it outranks the
  // expression Liquid shipped. On a sold-out product that expression is the
  // broken `variantId: ,` form, and stash-wins would have restored it verbatim.
  applyWishlistButtonState(wishlistButton, currentVariantId, productId, nextState, {
    forceAddOnclickRebuild: true,
  });
};

window.ensureSingleWishlist = (defaultName = 'My Wishlist', callbackFn, errorFn) => {
  window._swat.fetchLists({
    callbackFn: (lists = []) => {
      const ordered = lists.slice().reverse();
      const selected = ordered[0];
      // ECSR-1003: reported to the caller so fetchWishlistContents knows whether
      // the list it is about to fetch is the shopper's ONLY list. This function
      // renames one list; it never merges the contents of the others, so with two
      // or more lists the cache built from this one is incomplete by construction.
      const listCount = Array.isArray(lists) ? lists.length : 0;

      if (!selected) {
        window._swat.createList(
          { lname: defaultName },
          (resp) => {
            const lid = resp?.lid;
            if (lid) window._swat.swymCustomWishlistSelectedListId = lid;
            if (callbackFn) callbackFn({ lid, created: true, renamed: false, listCount: 1 });
          },
          errorFn || ((err) => console.error('Error creating single wishlist', err))
        );
        return;
      }

      const lid = selected.lid;
      window._swat.swymCustomWishlistSelectedListId = lid;

      if (defaultName && selected.lname && selected.lname !== defaultName) {
        window._swat.updateListDetails(
          { lid, lname: defaultName },
          () => {
            if (callbackFn) callbackFn({ lid, created: false, renamed: true, listCount });
          },
          errorFn || ((err) => console.error('Error renaming wishlist', err))
        );
      } else {
        if (callbackFn) callbackFn({ lid, created: false, renamed: false, listCount });
      }
    },
    errorFn: errorFn || ((err) => console.error('Error fetching lists', err))
  });
};

// ECSR-1002: re-apply the cached wishlist state to the hearts inside a subtree
// that has just been re-rendered. Defaults to the cart drawer: that is the only
// markup a cart update replaces (snippets/cart-drawer.liquid renders the mini
// cart hearts), and sweeping the whole document on every quantity nudge touched
// every product card on a collection page for nothing.
window.resyncWishlistButtons = (root) => {
  if (!Array.isArray(window.wishlistContents)) return;
  const scope = root || document.querySelector('cart-drawer');
  if (!scope) return;
  // ECSR-1003: this used to return early unless the cache was confirmed loaded,
  // which parked the resync for the WHOLE session after a failed fetch (there is
  // no retry, by design) while rememberWishlistItem kept recording genuinely saved
  // items. A cart update that REPLACES drawer markup — add-to-cart, a quantity
  // change — then shipped fresh Liquid hearts, unfilled and wired to add, and
  // nothing ever restored them: the next tap on a saved item saved a duplicate.
  // Per the contract, sweep either way; while the cache is unconfirmed the sweep
  // is additive, so it re-fills exactly the items this session saved and touches
  // no other heart.
  window.updateWishlistButtonsFromContents(window.wishlistContents, scope, {
    additive: !window.wishlistCacheIsAuthoritative()
  });
};

window.getSingleWishlistLid = (callbackFn, errorFn, defaultName = 'My Wishlist') => {
  window.ensureSingleWishlist(defaultName, (res) => {
    if (callbackFn) callbackFn(res?.lid);
  }, errorFn);
};

(function () {
  // ECSR-1002: the mini cart's hearts are Liquid-rendered and always ship
  // onclick="addToWishlist(...)" in their unsaved state, so every cart update
  // that re-renders snippets/cart-drawer.liquid wipes the saved state we applied
  // after the wishlist fetch. Criterion 1 names the mini cart, so resync the
  // hearts from the cached wishlist whenever the theme announces a completed
  // cart update — the same pub-sub event assets/cart-drawer.js listens to, which
  // the cart and product-form paths publish after their sections are replaced.
  // No polling and no MutationObserver: the theme already publishes this.
  // ECSR-1003: the cache is an empty array from the first line of this file, so
  // "not an array" no longer marks it as unloaded and cannot be what protects this
  // sweep. resyncWishlistButtons runs on every cart update and obeys the
  // positive-only contract: before the first fetch resolves, and forever after one
  // that failed, it only re-fills hearts for items this session saved and clears
  // nothing — so a heart survives a cart update whether or not that update replaced
  // the drawer markup.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      // ECSR-1002: assets/pubsub.js publishes synchronously and this subscriber
      // is registered before CartDrawer's own (wishlistSetup.js is deferred
      // ahead of cart-drawer.js in layout/theme.liquid), so anything thrown here
      // would stop the drawer opening and show the shopper a cart error.
      // Wishlist state must never be able to break the cart.
      try {
        window.resyncWishlistButtons();
      } catch (e) {
        console.error('Wishlist resync after cart update failed', e);
      }
    });
  }

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(function () {
    window.ensureSingleWishlist(DEFAULT_WISHLIST_NAME, function (res) {
      if (res?.lid) {
        window._swat.swymCustomWishlistSelectedListId = res.lid;
        // ECSR-1003: the cache may only call itself authoritative when this list
        // is the shopper's only list (see fetchWishlistContents).
        window.fetchWishlistContents(res.lid, { coversAllLists: res.listCount === 1 });
      }
    });
  });

  window.SwymCallbacks.push(function () {
    window._swat.evtLayer.addEventListener('sw:addedtocart', function () {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'wishlist' });
    });

    window._swat.evtLayer.addEventListener('sw:addedtolist', function (event) {
      const { epi: variantId, empi: productId, du: url } = event.detail || {};
      if (variantId && productId) {
        // ECSR-1003: Swym's own add path (and any add we did not initiate) has
        // to reach the cache too, otherwise the next cache-driven sweep clears
        // the heart this line just filled.
        window.rememberWishlistItem({ variantId, productId, url });
        updateWishlistButtonState(variantId, productId, true);
      }
    });

    window._swat.evtLayer.addEventListener('sw:removedfromlist', function (event) {
      const { epi: variantId, empi: productId } = event.detail || {};
      if (variantId && productId) {
        // ECSR-1003: a removal made in Swym's own UI has to leave the cache too.
        // Without this the cache kept a stale saved entry and the next sweep
        // re-filled a heart for an item that is no longer in the wishlist.
        window.forgetWishlistItem({ variantId, productId });
        updateWishlistButtonState(variantId, productId, false);
      }
    });
  });

  document.addEventListener('DOMContentLoaded', function() {
    const variantInput = document.querySelector('.product-variant-id');
    if (variantInput) {
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            updateProductFormWishlistButton();
          }
        });
      });
      
      observer.observe(variantInput, { 
        attributes: true, 
        attributeFilter: ['value'] 
      });
      
      variantInput.addEventListener('change', updateProductFormWishlistButton);
    }

    document.addEventListener('react-variant-change', function(event) {
      updateProductFormWishlistButton();
    });
  });
})();